"use client";
// Imports
import Link from "next/link";
import { Container, ComponentWrapper, Img } from "@/components";
import { Button } from "@/components/ui/button";
import { cva } from "class-variance-authority";
import { cn, resolveLink } from "@/lib/utils";

const tileStyles = cva(
  "flex flex-col relative overflow-hidden rounded-lg h-full",
  {
    variants: {
      width: {
        1: "col-span-12 sm:col-span-6 lg:col-span-3",
        2: "col-span-12 lg:col-span-4",
        3: "col-span-12 lg:col-span-8",
        4: "col-span-12 lg:col-span-6",
        5: "col-span-12",
      },
    },
  }
);

function linkLabelText(label) {
  if (!label) return "";
  // Preserve CMS casing; only strip a trailing arrow we add in the UI
  return label.trim().replace(/\s*(→|‣|›|->)\s*$/, "");
}

function ArrowLinkLabel({ label }) {
  const text = linkLabelText(label);
  if (!text) return null;

  return (
    <span className="inline-flex items-center gap-1.5">
      {text}
      <span
        aria-hidden
        className="inline-block transition-transform duration-300 ease-out group-hover/tile:translate-x-1.5 motion-reduce:transition-none motion-reduce:group-hover/tile:translate-x-0"
      >
        →
      </span>
    </span>
  );
}

function TileLink({ href, className, style, children }) {
  const link = resolveLink(href);
  if (!link) {
    return (
      <div className={cn(className, "pointer-events-none")} style={style}>
        {children}
      </div>
    );
  }
  if (link.includes("http")) {
    return (
      <a href={link} className={className} style={style}>
        {children}
      </a>
    );
  }
  return (
    <Link href={link} className={className} style={style}>
      {children}
    </Link>
  );
}

function imageSizes(width) {
  if (width === 1) return "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw";
  if (width === 2) return "(max-width: 1024px) 100vw, 33vw";
  if (width === 3) return "(max-width: 1024px) 100vw, 66vw";
  if (width === 4) return "(max-width: 1024px) 100vw, 50vw";
  return "100vw";
}

function tileBackground(tile, overrideTheme) {
  if (overrideTheme && tile?.background_tile_color) {
    return tile.background_tile_color;
  }
  return undefined;
}

function tileForeground(tile, overrideTheme) {
  if (overrideTheme && tile?.foreground_tile_color) {
    return tile.foreground_tile_color;
  }
  return undefined;
}

function tileButtonColors(tile) {
  if (!tile?.use_custom_colours) return undefined;
  return {
    background: tile?.custom_button_background_color,
    foreground: tile?.custom_button_foreground_color,
  };
}

function tileButtonVariant(tile) {
  const style =
    typeof tile?.button_style === "object"
      ? tile?.button_style?.value
      : tile?.button_style;
  return style === "outline" ? "outline" : "default";
}

function FeaturedTileCard({ tile, width, overrideTheme }) {
  const hasImage = Boolean(tile?.image);
  const bg = tileBackground(tile, overrideTheme);
  const fg = tileForeground(tile, overrideTheme);
  const isColourCard = !hasImage && Boolean(bg);
  const showBorder = Boolean(tile?.show_border);
  const borderColor = tile?.border_color || "#E7E4D9";

  const cardStyle = {
    ...(bg ? { backgroundColor: bg } : null),
    ...(fg ? { color: fg } : null),
    ...(showBorder ? { borderColor, borderWidth: 1, borderStyle: "solid" } : null),
  };

  return (
    <TileLink
      href={tile?.link}
      style={Object.keys(cardStyle).length ? cardStyle : undefined}
      className={cn(
        tileStyles({ width }),
        "cursor-pointer group/tile",
        !bg && "bg-white",
        showBorder && "border"
      )}
    >
      {hasImage && (
        <div className="relative aspect-[16/9] w-full overflow-hidden rounded-t-lg">
          <Img
            data={tile.image}
            fill
            sizes={imageSizes(width)}
            className="object-cover transition-transform duration-500 group-hover/tile:scale-[1.03]"
          />
        </div>
      )}
      <div
        className={cn(
          "flex flex-col flex-1 justify-between gap-5",
          isColourCard ? "p-6 lg:p-8 min-h-[14rem] lg:min-h-[16rem]" : "p-5 lg:p-6",
          hasImage && !bg && "bg-white rounded-b-lg"
        )}
      >
        <div className="flex flex-col">
          {tile?.subtitle && (
            <p
              className={cn(
                "font-body uppercase text-md lg:text-lg mb-2",
                isColourCard ? "text-brand-orange" : "text-brand-red-gum"
              )}
            >
              {tile.subtitle}
            </p>
          )}
          {tile?.title && (
            <h3
              className={cn(
                "font-heading font-heading-sentence leading-tight text-pretty",
                isColourCard ? "text-lg lg:text-xl" : "text-lg",
                !fg && (isColourCard ? "text-brand-wool" : "text-brand-espresso")
              )}
              style={fg ? { color: fg } : undefined}
            >
              {tile.title}
            </h3>
          )}
          {tile?.description && (
            <p
              className={cn(
                "font-body leading-snug mt-2 text-pretty",
                isColourCard ? "text-md lg:text-lg" : "text-sm lg:text-base",
                !fg && (isColourCard ? "text-brand-wool" : "text-brand-espresso")
              )}
              style={fg ? { color: fg } : undefined}
            >
              {tile.description}
            </p>
          )}
        </div>
        {!tile?.show_info && tile?.button_label && isColourCard && (
          <div className="self-start shrink-0">
            <Button
              size="default"
              variant={tileButtonVariant(tile)}
              customColors={tileButtonColors(tile)}
              tabIndex={-1}
              className="pointer-events-none !uppercase"
            >
              {tile.button_label}
            </Button>
          </div>
        )}
        {!tile?.show_info && tile?.button_label && !isColourCard && (
          <p className="shrink-0 pr-2 font-body font-bold text-brand-red-gum text-sm lg:text-base transition-colors duration-300 group-hover/tile:text-brand-orange">
            <ArrowLinkLabel label={tile.button_label} />
          </p>
        )}
        {tile?.show_info && tile?.info && (
          <p
            className={cn(
              "font-body text-sm shrink-0",
              !fg && "text-brand-espresso"
            )}
            style={fg ? { color: fg } : undefined}
          >
            {tile.info}
          </p>
        )}
      </div>
    </TileLink>
  );
}

function tileWidth(type) {
  switch (type) {
    case "1_4_tile":
      return 1;
    case "1_3_tile":
      return 2;
    case "2_3_tile":
      return 3;
    case "1_2_tile":
      return 4;
    case "full_tile":
      return 5;
    default:
      return 2;
  }
}

const SPACING_TOP = {
  none: "pt-0",
  small: "pt-3 lg:pt-4",
  medium: "pt-6 lg:pt-10",
  large: "pt-10 lg:pt-16",
  extra_large: "pt-16 lg:pt-24",
};

const SPACING_BOTTOM = {
  none: "pb-0",
  small: "pb-3 lg:pb-4",
  medium: "pb-6 lg:pb-10",
  large: "pb-10 lg:pb-16",
  extra_large: "pb-16 lg:pb-24",
};

function resolveSpacingValue(spacing) {
  if (!spacing) return "medium";
  return typeof spacing === "object" ? spacing.value : spacing;
}

// Featured Tiles — media cards or colour CTA tiles with optional borders
export function FeaturedTiles({
  type,
  tiles,
  sectionTitle,
  linkLabel,
  link,
  spacingTop,
  spacingBottom,
  sectionBackgroundColor,
  sectionForegroundColor,
  overrideTheme,
}) {
  const sectionHref = resolveLink(link);
  const topKey = resolveSpacingValue(spacingTop);
  const bottomKey = resolveSpacingValue(spacingBottom);
  const hasSectionBg = Boolean(sectionBackgroundColor);
  const titleColor = sectionForegroundColor || (hasSectionBg ? "#E7E4D9" : undefined);

  return (
    <ComponentWrapper
      type={type}
      className={cn(
        SPACING_TOP[topKey] ?? SPACING_TOP.medium,
        SPACING_BOTTOM[bottomKey] ?? SPACING_BOTTOM.medium
      )}
    >
      <div
        className={cn("w-full", hasSectionBg && "py-10 lg:py-14")}
        style={hasSectionBg ? { backgroundColor: sectionBackgroundColor } : undefined}
      >
        <Container>
          {(sectionTitle || (sectionHref && linkLabel)) && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-6 lg:mb-8">
              {sectionTitle && (
                <h2
                  className={cn(
                    "font-heading font-heading-sentence text-xl lg:text-[2.5rem] leading-none",
                    !titleColor && "text-brand-espresso"
                  )}
                  style={titleColor ? { color: titleColor } : undefined}
                >
                  {sectionTitle}
                </h2>
              )}
              {sectionHref && linkLabel && (
                <Link
                  href={sectionHref}
                  className={cn(
                    "group/tile font-body font-bold text-lg leading-none shrink-0 transition-colors",
                    !titleColor &&
                      "text-brand-red-gum hover:text-brand-orange"
                  )}
                  style={titleColor ? { color: titleColor } : undefined}
                >
                  <ArrowLinkLabel label={linkLabel} />
                </Link>
              )}
            </div>
          )}

          <div className="grid grid-cols-12 gap-6 w-full lg:gap-8">
            {tiles.map((tile, index) => (
              <FeaturedTileCard
                key={tile?.id ?? index}
                tile={tile}
                width={tileWidth(tile?.type)}
                overrideTheme={overrideTheme}
              />
            ))}
          </div>
        </Container>
      </div>
    </ComponentWrapper>
  );
}
